import * as React from 'react';

import { NodeProps, Position } from '@xyflow/react';
import { GitBranch } from 'lucide-react';

import { BaseNode } from '../../BaseNode';
import type { FlowNode, NodeVisualDefinition, HandleConfig } from '../../types';

import { SwitchConfigType, getSwitchPreviewText } from './schema';

/** Typed node for switch */
type SwitchNodeType = FlowNode<SwitchConfigType>;

/**
 * Calculate handle positions for multiple bottom handles
 * Distributes handles evenly across the bottom
 */
const calculateHandlePositions = (count: number): string[] => {
  if (count === 0) return [];
  if (count === 1) return ['left-1/2!'];

  // For 2+ handles, distribute evenly with padding on sides
  const positions: string[] = [];
  const step = 100 / (count + 1);

  for (let i = 1; i <= count; i++) {
    positions.push(`left-[${step * i}%]!`);
  }

  return positions;
};

/**
 * Generate dynamic handles based on switch cases
 */
const generateSwitchHandles = (config: SwitchConfigType): HandleConfig[] => {
  const handles: HandleConfig[] = [
    {
      type: 'target',
      position: Position.Top,
      className: 'bg-gray!',
    },
  ];

  // Add source handles for each case
  const cases = config.cases || [];
  const totalHandles = cases.length + 1; // cases + default
  const positions = calculateHandlePositions(totalHandles);

  cases.forEach((caseItem, index) => {
    handles.push({
      type: 'source',
      position: Position.Bottom,
      id: caseItem.value || `case_${index}`,
      className: `${positions[index]} bg-gray!`,
    });
  });

  // Add default handle
  handles.push({
    type: 'source',
    position: Position.Bottom,
    id: 'default',
    className: `${positions[positions.length - 1]} bg-gray!`,
  });

  return handles;
};

/**
 * Custom getContent function that shows case labels
 */
const getSwitchContent = (config: Record<string, unknown>): string => {
  const switchConfig = config as SwitchConfigType;

  if (!switchConfig.expression) {
    return 'Configure switch';
  }

  const cases = switchConfig.cases || [];
  const labels = cases
    .map((c) => c.label || c.value)
    .filter(Boolean)
    .slice(0, 3)
    .join(', ');

  if (labels) {
    return `Cases: ${labels}${cases.length > 3 ? '...' : ''} + default`;
  }

  return getSwitchPreviewText(switchConfig);
};

/**
 * Visual component for Switch nodes on the canvas.
 * Uses BaseNode with DYNAMIC handles based on case configuration.
 */
export const SwitchNode = React.memo<NodeProps<SwitchNodeType>>((props) => {
  const config = props.data.config as SwitchConfigType;

  // Create a dynamic visual definition with handles based on current config
  const dynamicDefinition: NodeVisualDefinition = React.useMemo(
    () => ({
      key: 'Switch',
      label: 'Switch',
      description: 'Multi-way branching based on expression value',
      icon: GitBranch,
      color: 'magenta',
      handles: generateSwitchHandles(config),
      getContent: getSwitchContent,
    }),
    [config.cases],
  );

  return (
    <BaseNode
      {...props}
      definition={dynamicDefinition}
      className="@SwitchNode"
    />
  );
});

SwitchNode.displayName = 'SwitchNode';

/**
 * Static visual definition for the Switch node (used in registry)
 * Note: The actual handles are generated dynamically in the SwitchNode component
 */
export const switchVisualDefinition: NodeVisualDefinition = {
  key: 'Switch',
  label: 'Switch',
  description: 'Multi-way branching based on expression value',
  icon: GitBranch,
  color: 'magenta',
  handles: [
    {
      type: 'target',
      position: Position.Top,
      className: 'bg-gray!',
    },
    // Default static handles (will be overridden by dynamic ones in component)
    {
      type: 'source',
      position: Position.Bottom,
      id: 'default',
      className: 'left-1/2! bg-gray!',
    },
  ],
  getContent: getSwitchContent,
};
